import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CatchFilterPanelComponent, FilterEvent, FilterQuery } from '../shared/components/catch-filter-panel/catch-filter-panel.component';
import { environment } from '../../environments/environment';
import * as mapboxgl from 'mapbox-gl';
import { FeatureCollection } from 'geojson';

interface FishingSpot {
  id: number;
  name: string | null;
  location: string | null;
  center_lat: number;
  center_lng: number;
  radius_meters: number | null;
  catch_count: number;
}

@Component({
  selector: 'app-maps',
  templateUrl: './maps.component.html',
  styleUrls: ['./maps.component.scss'],
  imports: [CommonModule, MatProgressSpinnerModule, CatchFilterPanelComponent],
  standalone: true
})
export class MapsComponent implements OnInit {
  map!: mapboxgl.Map;
  popup!: mapboxgl.Popup;

  // Map configuration
  style = 'mapbox://styles/mapbox/standard-satellite';
  lat = 39.001904;
  lng = -76.430749;

  heatMap = false;
  isLoading = true;

  private isMapLoaded = false;
  private pendingFilterQuery: FilterQuery | null = null;

  constructor(
    private http: HttpClient,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.initializeMap();
  }

  private initializeMap(): void {
    this.map = new mapboxgl.Map({
      accessToken: environment.mapbox.accessToken,
      container: 'map',
      style: this.style,
      center: [this.lng, this.lat],
      zoom: 13,
      dragRotate: false,
      touchPitch: false
    });

    // Create popup for later use
    this.popup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false
    });

    this.map.on('load', () => {
      this.map.addSource('fish-data', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        }
      });

      this.addFishLayers();
      this.addPopupInteraction();
      this.loadSpots();
      
      this.isMapLoaded = true;
      if (this.pendingFilterQuery) {
        this.applyFilters(this.pendingFilterQuery);
        this.pendingFilterQuery = null;
      }

      // Wait for layout to finish before hiding overall loading spinner
      this.isLoading = false;
    });
  }

  private buildSpotCircleGeoJSON(lat: number, lng: number, radiusM: number): GeoJSON.Feature {
    const R = 6378137;
    const pts = 64;
    const coords: [number, number][] = [];
    for (let i = 0; i <= pts; i++) {
      const angle = (i * 360 / pts) * Math.PI / 180;
      const dLat = (radiusM / R) * (180 / Math.PI);
      const dLng = (radiusM / (R * Math.cos(Math.PI * lat / 180))) * (180 / Math.PI);
      coords.push([lng + dLng * Math.cos(angle), lat + dLat * Math.sin(angle)]);
    }
    return { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [coords] } };
  }

  private loadSpots(): void {
    this.http.get<FishingSpot[]>(`${environment.apiUrl}/fishing_spots`).subscribe({
      next: (spots) => {
        const circleFeatures: GeoJSON.Feature[] = spots
          .filter(s => s.radius_meters)
          .map(s => ({
            ...this.buildSpotCircleGeoJSON(s.center_lat, s.center_lng, s.radius_meters!),
            properties: { id: s.id, name: s.name, location: s.location, catch_count: s.catch_count },
          }));
        const centerFeatures: GeoJSON.Feature[] = spots.map(s => ({
          type: 'Feature' as const,
          properties: { id: s.id, name: s.name || s.location || `Spot #${s.id}`, catch_count: s.catch_count },
          geometry: { type: 'Point' as const, coordinates: [s.center_lng, s.center_lat] },
        }));

        this.map.addSource('spot-circles', { type: 'geojson', data: { type: 'FeatureCollection', features: circleFeatures } });
        this.map.addSource('spot-centers', { type: 'geojson', data: { type: 'FeatureCollection', features: centerFeatures } });

        this.map.addLayer({
          id: 'spot-fill',
          type: 'fill',
          source: 'spot-circles',
          paint: { 'fill-color': '#ffcc00', 'fill-opacity': 0.08 },
        }, 'fishes');
        this.map.addLayer({
          id: 'spot-outline',
          type: 'line',
          source: 'spot-circles',
          paint: { 'line-color': '#ffcc00', 'line-width': 1.5, 'line-dasharray': [3, 3] },
        }, 'fishes');
        this.map.addLayer({
          id: 'spot-labels',
          type: 'symbol',
          source: 'spot-centers',
          layout: {
            'text-field': ['get', 'name'],
            'text-size': 11,
            'text-anchor': 'top',
            'text-offset': [0, 0.5],
          },
          paint: {
            'text-color': '#ffcc00',
            'text-halo-color': 'rgba(0,0,0,0.7)',
            'text-halo-width': 1,
          },
        });

        this.map.on('click', 'spot-fill', (e) => {
          if (this.map.queryRenderedFeatures(e.point, { layers: ['fishes'] }).length > 0) return;
          const spotId = e.features?.[0]?.properties?.['id'];
          if (spotId) this.router.navigate(['/spot', spotId]);
        });
        this.map.on('mouseenter', 'spot-fill', () => { this.map.getCanvas().style.cursor = 'pointer'; });
        this.map.on('mouseleave', 'spot-fill', () => { this.map.getCanvas().style.cursor = ''; });
      },
      error: () => {}
    });
  }

  onFilterChange(event: FilterEvent): void {
    if (!this.map || !this.isMapLoaded) {
      this.pendingFilterQuery = event.query;
      return;
    }
    this.applyFilters(event.query);
  }

  private applyFilters(query: FilterQuery): void {
    this.isLoading = true;
    this.http.post<FeatureCollection>(`${environment.apiUrl}/catches`, query).subscribe({
      next: (data) => {
        if (this.map.getSource('fish-data')) {
           (this.map.getSource('fish-data') as mapboxgl.GeoJSONSource).setData(data);
        }
        this.isLoading = false;
      },
      error: () => { this.isLoading = false; }
    });
  }

  addFishLayers(): void {
    this.map.addLayer({
      id: 'fishes',
      type: 'circle',
      source: 'fish-data',
      paint: {
        'circle-radius': 5,
        'circle-color': ['get', 'fish_type_display_color'],
        'circle-stroke-width': 1,
        'circle-stroke-color': '#ffffff',
      },
    });

    this.map.addLayer({
      id: 'heatMap',
      type: 'heatmap',
      source: 'fish-data',
      filter: ['!=', 'fish_type', 'no_fish'],
      layout: { 'visibility': 'none' },
      paint: {
        'heatmap-color': [
          'interpolate',
          ['linear'],
          ['heatmap-density'],
          0,
          'rgba(33,102,172,0)',
          0.5,
          'rgb(103,169,207)',
          0.9,
          'rgb(239,138,98)',
          1,
          'rgb(178,24,43)'
      ],
      'heatmap-opacity': 0.8,
      },
    });
  }

  private addPopupInteraction(): void {
    this.map.on('mousemove', 'fishes', (e) => {
      this.map.getCanvas().style.cursor = 'pointer';

      if (e.features && e.features.length > 0) {
        const feature = e.features[0];

        if (feature.geometry.type === 'Point' && 'coordinates' in feature.geometry) {
          const pointGeometry = feature.geometry as GeoJSON.Point;
          const popupContent = `
            <div>
            <strong>Fish Type:</strong> ${feature.properties?.['fish_type_full_name'] ?? 'Unknown'}<br>
            <strong>Temperature:</strong> ${feature.properties?.['temperature'] ?? 'N/A'}°F<br>
            <strong>Water Temp:</strong> ${feature.properties?.['water_temperature'] ?? 'N/A'}°F ${feature.properties?.['water_temperature_trend'] ?? ''}<br>
            <strong>Tide:</strong> ${feature.properties?.['tide_cycle'] ?? 'Unknown'}<br>
            <strong>Wind:</strong> ${feature.properties?.['wind_speed'] ?? 'N/A'} ${feature.properties?.['wind_direction'] ?? ''}<br>
            <strong>Forecast:</strong> ${feature.properties?.['short_forecast'] ?? 'No forecast'}<br>
            <strong>Time:</strong> ${feature.properties?.['time'] ? new Date(feature.properties['time']).toLocaleString() : 'Unknown'}
            </div>
          `;

          this.popup
            .setLngLat(pointGeometry.coordinates as [number, number])
            .setHTML(popupContent)
            .addTo(this.map);
        }
      }
    });

    this.map.on('mouseleave', 'fishes', () => {
      this.map.getCanvas().style.cursor = '';
      this.popup.remove();
    });

    this.map.on('click', 'fishes', (e) => {
      const feature = e.features?.[0];
      if (!feature) return;
      const catchId = feature.id;
      if (!catchId) return;
      const source = this.map.getSource('fish-data') as mapboxgl.GeoJSONSource;
      const data = (source as any)._data as GeoJSON.FeatureCollection;
      const ids = (data?.features ?? []).map((f: any) => f.id).filter(Boolean);
      this.router.navigate(['/catch', catchId], { queryParams: { list: ids.join(',') } });
    });
  }

  onHeatmapToggle(enabled: boolean): void {
    if (this.map && this.map.getLayer('heatMap')) {
      this.map.setLayoutProperty('heatMap', 'visibility', enabled ? 'visible' : 'none');
    }
  }
}
